import { RundotApiError } from '../errors'
import {
  COMPOSE_POST_MAX_TEXT_LENGTH,
  COMPOSE_POST_MAX_TITLE_LENGTH,
  COMPOSE_POST_SUBREDDIT_PATTERN,
  SHARE_FILE_ALLOWED_MIME_TYPES,
  SHARE_FILE_MAX_SIZE_BYTES,
} from './SocialApi'

// Mirrors server/cloud-run/src/utils/shareParamsValidation.ts and the
// validateShareMetadata / validateClickMetadata caps in
// server/cloud-run/src/controllers/shares.controller.ts. shareParams/metadata/
// click failures surface as a RundotApiError to the game; shareFile failures are
// thrown client-side as plain Errors by the production impls, so we mirror those
// exactly (see RpcSocialApi.ts / HttpSocialApi.ts).
const SHARE_PARAMS_MAX_KEYS = 20
const SHARE_PARAMS_MAX_VALUE_LENGTH = 100 * 1024
const SHARE_PARAMS_MAX_TOTAL_SIZE = 200 * 1024
const META_MAX_TITLE = 200
const META_MAX_DESCRIPTION = 1000
const META_MAX_IMAGE_URL = 2048
const CLICK_MAX_KEYS = 20
const CLICK_MAX_KEY_LENGTH = 64
const CLICK_MAX_VALUE_LENGTH = 1024
const CLICK_MAX_TOTAL_SIZE = 4096

function invalid(message: string): never {
  throw new RundotApiError('UNKNOWN', message, 0)
}

export function validateShareParams(shareParams: unknown): void {
  // Server validateShareParams (shareParamsValidation.ts:8-10) returns early for
  // null/undefined — accept, don't reject.
  if (shareParams === undefined || shareParams === null) return
  if (typeof shareParams !== 'object' || Array.isArray(shareParams)) {
    invalid('shareParams must be a plain object')
  }
  const entries = Object.entries(shareParams as Record<string, unknown>)
  if (entries.length > SHARE_PARAMS_MAX_KEYS) {
    invalid(`shareParams exceeds maximum of ${SHARE_PARAMS_MAX_KEYS} keys`)
  }
  for (const [key, value] of entries) {
    if (typeof value !== 'string') invalid(`shareParams value for key "${key}" must be a string`)
    if (value.length > SHARE_PARAMS_MAX_VALUE_LENGTH) {
      invalid(`shareParams value for key "${key}" exceeds maximum length of 100KB`)
    }
  }
  // Mirror the server EXACTLY: shareParamsValidation.ts:33 uses
  // JSON.stringify(shareParams).length (UTF-16 code units), NOT UTF-8 bytes.
  if (JSON.stringify(shareParams).length > SHARE_PARAMS_MAX_TOTAL_SIZE) {
    invalid('shareParams exceeds maximum serialized size of 200KB')
  }
}

// setShareIntentAsync requires shareParams (unlike shareLinkAsync, where the
// server tolerates null/undefined). Enforce presence here so every
// environment — bridge, mock, playground — rejects identically; a game must
// not pass in local dev and then fail in the released app.
export function validateShareIntentParams(shareParams: unknown): void {
  if (shareParams === undefined || shareParams === null) {
    invalid('shareParams is required')
  }
  validateShareParams(shareParams)
}

// Mirrors the host's `parseShareTarget` (client H5SocialHandler), so bridge,
// mock, and playground all reject the same payloads. The app id is added
// host-side, so there is nothing to check it against here.
//
// Unlike shareParams/metadata above, `null` is rejected rather than treated as
// absent: both the host and the server's `isShareTarget` reject it, so
// accepting it here would mint a plain app share in the playground and throw
// in the released app.
export function validateShareTarget(target: unknown): void {
  if (target === undefined) return
  if (typeof target !== 'object' || target === null || Array.isArray(target)) {
    invalid('target must be a plain object')
  }
  const { format, entryId } = target as { format?: unknown; entryId?: unknown }
  if (format !== 'interactive-fiction' && format !== 'video') {
    invalid("target.format must be 'interactive-fiction' or 'video'")
  }
  if (typeof entryId !== 'string' || entryId.length === 0) {
    invalid('target.entryId must be a non-empty string')
  }
  if (entryId.includes(':')) invalid('target.entryId must not contain ":"')
}

export function validateShareMetadata(metadata: unknown): void {
  if (metadata === undefined || metadata === null) return
  if (typeof metadata !== 'object' || Array.isArray(metadata)) invalid('metadata must be a plain object')
  const m = metadata as { title?: unknown; description?: unknown; imageUrl?: unknown }
  // Order matches shares.controller.ts validateShareMetadata exactly: ALL type
  // checks first, THEN all length checks.
  if (m.title !== undefined && typeof m.title !== 'string') invalid('metadata.title must be a string')
  if (m.description !== undefined && typeof m.description !== 'string') invalid('metadata.description must be a string')
  if (m.imageUrl !== undefined && typeof m.imageUrl !== 'string') invalid('metadata.imageUrl must be a string')
  if (typeof m.title === 'string' && m.title.length > META_MAX_TITLE) invalid(`metadata.title exceeds maximum length of ${META_MAX_TITLE}`)
  if (typeof m.description === 'string' && m.description.length > META_MAX_DESCRIPTION) invalid(`metadata.description exceeds maximum length of ${META_MAX_DESCRIPTION}`)
  if (typeof m.imageUrl === 'string' && m.imageUrl.length > META_MAX_IMAGE_URL) invalid(`metadata.imageUrl exceeds maximum length of ${META_MAX_IMAGE_URL}`)
}

export function validateClickMetadata(metadata: unknown): void {
  if (typeof metadata !== 'object' || metadata === null) invalid('metadata must be a non-null object')
  const entries = Object.entries(metadata as Record<string, unknown>)
  if (entries.length > CLICK_MAX_KEYS) invalid(`metadata exceeds maximum of ${CLICK_MAX_KEYS} keys`)
  for (const [key, value] of entries) {
    if (typeof value !== 'string') invalid(`metadata value for key "${key}" must be a string`)
    if (key.length > CLICK_MAX_KEY_LENGTH) invalid(`metadata key "${key}" exceeds maximum length of ${CLICK_MAX_KEY_LENGTH}`)
    if (value.length > CLICK_MAX_VALUE_LENGTH) invalid(`metadata value for key "${key}" exceeds maximum length of ${CLICK_MAX_VALUE_LENGTH}`)
  }
  // Server uses JSON.stringify(metadata).length (shares.controller.ts:24-25),
  // NOT UTF-8 bytes — mirror exactly (message keeps the "bytes" wording).
  if (JSON.stringify(metadata).length > CLICK_MAX_TOTAL_SIZE) {
    invalid(`metadata total size exceeds maximum of ${CLICK_MAX_TOTAL_SIZE} bytes`)
  }
}

// Strip C0/C1 control characters (keep \n so multi-line captions survive).
// Re-applied host-side in client H5SocialHandler — keep in sync.
export function stripComposeControlChars(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g, '')
}

// Compose failures are thrown client-side as plain Errors, same convention as
// validateShareFile below. Expects text/title already control-char-stripped.
export function validateComposePost(post: { text: unknown; title?: unknown; subreddit?: unknown }): void {
  if (typeof post.text !== 'string' || post.text.trim().length === 0) {
    throw new Error('text must be a non-empty string')
  }
  if (post.text.length > COMPOSE_POST_MAX_TEXT_LENGTH) {
    throw new Error(`text exceeds maximum length of ${COMPOSE_POST_MAX_TEXT_LENGTH}`)
  }
  if (post.title !== undefined) {
    if (typeof post.title !== 'string') throw new Error('title must be a string')
    if (post.title.length > COMPOSE_POST_MAX_TITLE_LENGTH) {
      throw new Error(`title exceeds maximum length of ${COMPOSE_POST_MAX_TITLE_LENGTH}`)
    }
  }
  if (post.subreddit !== undefined) {
    if (typeof post.subreddit !== 'string' || !COMPOSE_POST_SUBREDDIT_PATTERN.test(post.subreddit)) {
      throw new Error('subreddit must be 1-21 letters, numbers, or underscores (no r/ prefix)')
    }
  }
}

export function validateShareFile(options: { data: Blob | ArrayBuffer | string; mimeType: string }): void {
  // Plain Error, messages identical to RpcSocialApi.ts:91-119 / HttpSocialApi.ts.
  const { data, mimeType } = options
  // Cast matches RpcSocialApi.ts:91 — the tuple's element type is a literal
  // union, so `.includes(mimeType: string)` needs the widening cast under strict TS.
  if (!(SHARE_FILE_ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType)) {
    throw new Error(
      `unsupported MIME type "${mimeType}". Allowed: ${SHARE_FILE_ALLOWED_MIME_TYPES.join(', ')}`,
    )
  }

  let sizeBytes: number
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    sizeBytes = data.size
  } else if (data instanceof ArrayBuffer) {
    sizeBytes = data.byteLength
  } else if (typeof data === 'string') {
    // Pre-encoded base64 string: derive byte size from its length (matches
    // RpcSocialApi.ts:111-119).
    const len = data.length
    const paddingChars = (data[len - 1] === '=' ? 1 : 0) + (data[len - 2] === '=' ? 1 : 0)
    sizeBytes = Math.floor((len * 3) / 4) - paddingChars
  } else {
    return
  }

  if (sizeBytes > SHARE_FILE_MAX_SIZE_BYTES) {
    throw new Error(`file size (${sizeBytes} bytes) exceeds max allowed (${SHARE_FILE_MAX_SIZE_BYTES} bytes)`)
  }
}
